// ⛔ LOCKED FILE — see server/payment-core/README.md
// Cart shape & price math. Changing fields here can break order totals.
import { useCallback, useEffect, useState } from "react";

export interface CartLine {
  productId: number;
  slug: string;
  nameHe: string;
  priceAgorot: number;
  qty: number;
  imageUrl: string | null;
}

const STORAGE_KEY = "kn.cart.v1";

function load(): CartLine[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CartLine[]) : [];
  } catch {
    return [];
  }
}

function save(lines: CartLine[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  window.dispatchEvent(new CustomEvent("cart:update"));
}

export function useCart() {
  const [lines, setLines] = useState<CartLine[]>(load);

  useEffect(() => {
    const onUpdate = () => setLines(load());
    window.addEventListener("cart:update", onUpdate);
    window.addEventListener("storage", onUpdate);
    return () => {
      window.removeEventListener("cart:update", onUpdate);
      window.removeEventListener("storage", onUpdate);
    };
  }, []);

  const add = useCallback((line: Omit<CartLine, "qty">, qty = 1) => {
    const current = load();
    const existing = current.find((l) => l.productId === line.productId);
    const next = existing
      ? current.map((l) => (l.productId === line.productId ? { ...l, qty: l.qty + qty } : l))
      : [...current, { ...line, qty }];
    save(next);
  }, []);

  const setQty = useCallback((productId: number, qty: number) => {
    const current = load();
    const next = qty <= 0
      ? current.filter((l) => l.productId !== productId)
      : current.map((l) => (l.productId === productId ? { ...l, qty } : l));
    save(next);
  }, []);

  const remove = useCallback((productId: number) => {
    save(load().filter((l) => l.productId !== productId));
  }, []);

  const clear = useCallback(() => save([]), []);

  const totalAgorot = lines.reduce((sum, l) => sum + l.priceAgorot * l.qty, 0);
  const totalQty = lines.reduce((sum, l) => sum + l.qty, 0);

  return { lines, totalAgorot, totalQty, add, setQty, remove, clear };
}
